// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { BudgetVerdict } from "@bitebalance/contracts"
import { useI18n, formatEurFromCents } from "@/i18n"
import { Button } from "@/components/ui/button"
import { InfoButton } from "@/components/ui/InfoButton"
import { openAmyWithPrompt } from "@/lib/amyLauncher"
import { glossaryTerm, goalStatusLabel, goalStatusWithDelta } from "@/lib/glossary"

/**
 * Shop = CHECKOUT truth (Budget Model v2, ADR-052 §11.3). Everything here
 * comes from the ONE server verdict on the shopping list — there is no
 * client-side cost model any more. When the list carries no verdict,
 * nothing budget-related renders.
 *
 * Owner invariant 2026-09-04 (one dataset per presentation): this verdict
 * is a LIVE recompute against the current pantry and catalog prices — a
 * different dataset from the stored rows the page lists. It therefore
 * renders as its OWN clearly labelled block ("Estimate with today's prices
 * and pantry" + the explanatory note) and is never the headline of the
 * visible rows. Inside the block:
 *
 *  - the live checkout amount — or, when the live basket itself is not
 *    fully priced, "Priced so far €X · N items still need a reliable
 *    price" (a partial subtotal is never a total);
 *  - checkout line: positive confirmation within the maximum, the exact
 *    overshoot when it exceeds it (hard — the only lever is Settings, plus
 *    "Ask Amy to trim"), "can't confirm" when a needed row is unpriced,
 *    nothing when no maximum is set;
 *  - a plain "your weekly budget of X currently limits every shop" note
 *    when the ceiling is an unclassified pre-v2 amount (no internal wording);
 *  - goal line: forecast weekly average vs the SOFT goal with the canonical
 *    status word and the EXACT signed delta (no tolerance band, owner D-3);
 *    omitted when there is no goal;
 *  - economics breakdown (food consumed this week / pantry carryover) ONLY
 *    when the server marks it complete (`economics.complete` or
 *    `pricing.complete`); otherwise the partial-economics note explains why
 *    the split is withheld (documented choice: hide + explain — a split
 *    summed over priced rows only would misstate both halves).
 */
export function BudgetVerdictPanel({ verdict }: { verdict: BudgetVerdict }) {
  const { t, locale } = useI18n()
  const checkout = verdict.checkout
  const goal = verdict.goal
  const economics = verdict.economics
  const pricing = verdict.pricing
  const money = (cents: number) => formatEurFromCents(cents, locale)

  const unclassifiedLimit = checkout?.maxSource === "legacy_limit"
  const liveCostCents = checkout?.costCents ?? verdict.weeklyCostCents
  // Ceiling-independent completeness of the LIVE basket. Without the
  // pricing block (pre-2026-09-04 server) fall back to the verdict's own
  // unpriced count — never assume complete.
  const liveComplete = pricing ? pricing.complete : verdict.unpricedItemCount === 0
  const liveUnpricedCount = pricing ? pricing.unpricedRequiredCount : verdict.unpricedItemCount
  const economicsComplete = economics?.complete === true || pricing?.complete === true

  return (
    <div
      className="flex flex-col gap-2 rounded-bb-lg border border-line bg-cream/60 px-4 py-3"
      data-testid="shop-budget-panel"
      aria-labelledby="shop-live-estimate-label"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span
          id="shop-live-estimate-label"
          data-testid="shop-live-label"
          className="flex items-center gap-1 text-xs font-semibold tracking-wide text-ink-2 uppercase"
        >
          {t("views.budget.liveEstimateLabel")}
          <InfoButton termId="next-shop-estimate" />
        </span>
        {liveComplete ? (
          <span className="text-base font-semibold text-ink" data-testid="shop-live-amount">
            ≈ {money(liveCostCents)}
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-1 text-sm text-ink" data-testid="shop-live-amount">
            <span className="text-ink-2">{t("views.budget.pricedSoFar")}</span>
            <span className="font-semibold">{money(pricing?.pricedSubtotalCents ?? liveCostCents)}</span>
            <span className="text-ink-2">· {t("views.budget.needsPriceCount", { count: liveUnpricedCount })}</span>
            <InfoButton termId="items-without-reliable-price" />
          </span>
        )}
      </div>
      <p className="text-xs text-ink-2" data-testid="shop-live-note">
        {t("views.budget.liveEstimateNote")}
      </p>

      {checkout && checkout.status !== "no_max" && (
        <div
          role={checkout.status === "exceeds_max" ? "alert" : "status"}
          className={
            "flex flex-wrap items-center gap-3 rounded-bb-lg border px-4 py-2.5 " +
            (checkout.status === "within_max"
              ? "border-green/40 bg-green/10"
              : "border-gold/50 bg-gold/10")
          }
        >
          <span className="flex items-center gap-1 text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t("views.budget.shopCheckoutTitle")}
          </span>
          <p className="flex flex-1 items-center gap-1 text-sm text-ink">
            <span>
              {checkout.status === "within_max" &&
                t("views.budget.shopWithinMax", {
                  cost: money(checkout.costCents),
                  under: money(Math.abs(checkout.deltaCents ?? 0)),
                  max: money(checkout.maxCents ?? 0),
                })}
              {checkout.status === "exceeds_max" &&
                t("views.budget.shopExceedsMax", {
                  cost: money(checkout.costCents),
                  over: money(Math.max(0, checkout.deltaCents ?? 0)),
                  max: money(checkout.maxCents ?? 0),
                })}
              {checkout.status === "unverifiable" &&
                t("views.budget.shopUnverifiableMax", {
                  count: liveUnpricedCount,
                  max: money(checkout.maxCents ?? 0),
                })}
            </span>
            <InfoButton
              termId={checkout.status === "unverifiable" ? "items-without-reliable-price" : "maximum-checkout"}
            />
          </p>
          {checkout.status === "exceeds_max" && (
            <Button
              type="button"
              className="min-h-9"
              onClick={() => openAmyWithPrompt(t("amy.greeting.promptCheaper"))}
            >
              {t("views.shop.budgetTrim")}
            </Button>
          )}
        </div>
      )}

      {unclassifiedLimit && checkout && (
        <p className="flex items-center gap-1 text-xs text-ink-2" data-testid="shop-current-budget-note">
          <span>{t("views.budget.shopLegacyLimitNote", { amount: money(checkout.maxCents ?? 0) })}</span>
          <InfoButton termId="maximum-checkout" />
        </p>
      )}

      {goal && goal.goalCents !== null && (
        <p className="flex items-center gap-1 text-xs text-ink-2" data-testid="shop-goal-line">
          <span>
            {goal.status === "unavailable" || goal.forecastWeeklyAverageCents === null
              ? t("views.budget.shopGoalUnavailable", {
                  reason:
                    goal.unavailableReason === "unpriced_rows"
                      ? glossaryTerm("items-without-reliable-price", locale).name
                      : goalStatusLabel("unavailable", locale),
                })
              : t("views.budget.shopGoalLine", {
                  forecast: money(goal.forecastWeeklyAverageCents),
                  goal: money(goal.goalCents),
                  status: goalStatusWithDelta(goal.status, goal.deltaCents, locale),
                })}
          </span>
          <InfoButton termId="forecast-weekly-average" />
        </p>
      )}

      {economics &&
        (economicsComplete ? (
          <dl className="grid grid-cols-2 gap-2 text-xs text-ink-2" data-testid="shop-economics">
            <div className="flex items-center gap-1">
              <dt>{glossaryTerm("food-consumed-this-week", locale).name}</dt>
              <InfoButton termId="food-consumed-this-week" />
              <dd className="font-semibold text-ink">{money(economics.consumedThisWeekValueCents)}</dd>
            </div>
            <div className="flex items-center gap-1">
              <dt>{glossaryTerm("pantry-carryover", locale).name}</dt>
              <InfoButton termId="pantry-carryover" />
              <dd className="font-semibold text-ink">{money(economics.pantryCarryoverValueCents)}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-xs text-ink-2" data-testid="shop-economics-partial">
            {t("views.budget.partialEconomicsNote")}
          </p>
        ))}
    </div>
  )
}

/**
 * Whether the live verdict earns its own block next to the visible rows:
 * only when it says something the rows do not — a checkout that differs
 * from the visible subtotal, a ceiling verdict, or a goal verdict.
 */
export function liveVerdictIsInformative(verdict: BudgetVerdict, visibleSubtotalCents: number): boolean {
  const liveCostCents = verdict.checkout?.costCents ?? verdict.weeklyCostCents
  if (liveCostCents !== visibleSubtotalCents) return true
  if (verdict.checkout && verdict.checkout.status !== "no_max") return true
  if (verdict.goal && verdict.goal.goalCents !== null) return true
  return false
}
